import { frameTypes, FrameData, ScaledFrameData } from "../constants/frameTypes";
import { scanmeBottomFrame } from "./scanme-bottom";
import { scanmeTopFrame } from "./scanme-top";
import { menuBottomFrame } from "./menu-bottom";
import { menuTopFrame } from "./menu-top";
import { arrowBottomFrame } from "./arrow-bottom";
import { arrowTopFrame } from "./arrow-top";

// Frame registry - maps frame types to their master configurations
export const frameRegistry: Record<string, FrameData> = {
  [frameTypes.SCANME_BOTTOM]: scanmeBottomFrame,
  [frameTypes.SCANME_TOP]: scanmeTopFrame,
  [frameTypes.MENU_BOTTOM]: menuBottomFrame,
  [frameTypes.MENU_TOP]: menuTopFrame,
  [frameTypes.ARROW_BOTTOM]: arrowBottomFrame,
  [frameTypes.ARROW_TOP]: arrowTopFrame
};

/**
 * Scale a frame to any requested width (maintaining aspect ratio)
 * This replaces the old size-specific approach with dynamic scaling
 */
export function getScaledFrameConfig(frameType: string, requestedWidth: number): ScaledFrameData {
  const frameData = frameRegistry[frameType];
  if (!frameData) {
    throw new Error(`Frame type "${frameType}" not found`);
  }

  // Calculate final height maintaining aspect ratio
  const frameWidth = requestedWidth;
  const frameHeight = Math.round(requestedWidth * (frameData.referenceHeight / frameData.referenceWidth));

  // Calculate QR position and size in pixels
  const qrX = Math.round(frameWidth * frameData.qrPosition.x);
  const qrY = Math.round(frameHeight * frameData.qrPosition.y);
  const qrSize = Math.round(frameWidth * frameData.qrSize);

  // Generate complete SVG with proper viewBox and dimensions
  const scaledSvg = createFrameSvg(frameData, frameWidth, frameHeight);

  return {
    width: frameWidth,
    height: frameHeight,
    qrPosition: {
      x: qrX,
      y: qrY
    },
    qrSize: qrSize,
    scaledSvg: scaledSvg
  };
}

/**
 * Create complete SVG with frame content and proper dimensions
 */
function createFrameSvg(frameData: FrameData, targetWidth: number, targetHeight: number): string {
  // Generate complete SVG with proper structure using reference dimensions
  return `<svg width="${targetWidth}" height="${targetHeight}" viewBox="0 0 ${frameData.referenceWidth} ${frameData.referenceHeight}" xmlns="http://www.w3.org/2000/svg">
    ${frameData.svgContent}
  </svg>`;
}

/**
 * Check if a frame type is available
 */
export function isFrameTypeAvailable(frameType: string): boolean {
  return frameType in frameRegistry;
}

/**
 * Get list of available frame types
 */
export function getAvailableFrameTypes(): string[] {
  return Object.keys(frameRegistry);
}

/**
 * Apply colors to frame SVG paths
 * Replaces .st0 and .st1 classes with actual colors
 */
export function applyFrameColors(svgContent: string, primaryColor: string, secondaryColor: string): string {
  // Define CSS classes for the frame
  const styles = `
    <defs>
      <style>
        .st0 { fill: ${primaryColor}; }
        .st1 { fill: ${secondaryColor}; }
      </style>
    </defs>
  `;

  return styles + svgContent;
}
